import { fmtTRY } from '../../yeni-design/primitives';
import { useSaSorgu } from '../useSatisVerisi';
import { sayi } from '../ozetHesaplari';
import { oranMetni } from '../bicim';
import type { IadeOraniSatiri } from '../uyarilar';
import type { DetayGovdeProps } from './detayTipleri';
import { bos, DetayMesaj, DetayYukleniyor } from './ortak';

export interface IadeTabloSatiri {
  lokasyon: string;
  iade: number;
  netCiro: number;
  oran: number | null;
  oncekiOran: number | null;
}

/**
 * Sube satirlari oran azalan. Toplam oran sube oranlarinin ortalamasi DEGIL, tutarlardan (uyarilar.iadeArtisi
 * ile ayni kural); onceki toplam yalniz onceki tutari olan subelerden.
 */
export function iadeTablosu(rows: IadeOraniSatiri[]): { satirlar: IadeTabloSatiri[]; toplam: IadeTabloSatiri } {
  let iade = 0;
  let net = 0;
  let oncekiIade = 0;
  let oncekiNet = 0;
  let oncekiVar = false;
  const satirlar = rows
    .map((r) => {
      iade += sayi(r.iade);
      net += sayi(r.net_ciro);
      if (!bos(r.onceki_net_ciro)) {
        oncekiVar = true;
        oncekiIade += sayi(r.onceki_iade);
        oncekiNet += sayi(r.onceki_net_ciro);
      }
      return {
        lokasyon: String(r.lokasyon),
        iade: sayi(r.iade),
        netCiro: sayi(r.net_ciro),
        oran: bos(r.oran) ? null : sayi(r.oran),
        oncekiOran: bos(r.onceki_oran) ? null : sayi(r.onceki_oran),
      };
    })
    .sort((a, b) => (b.oran ?? -1) - (a.oran ?? -1));
  return {
    satirlar,
    toplam: {
      lokasyon: 'Toplam',
      iade,
      netCiro: net,
      oran: net > 0 ? (iade / net) * 100 : null,
      oncekiOran: oncekiVar && oncekiNet > 0 ? (oncekiIade / oncekiNet) * 100 : null,
    },
  };
}

function Satir({ s, testid, kalin = false }: { s: IadeTabloSatiri; testid: string; kalin?: boolean }) {
  const artti = s.oran !== null && s.oncekiOran !== null && s.oran > s.oncekiOran;
  return (
    <tr data-testid={testid} className={`border-b border-border last:border-b-0 ${kalin ? 'font-semibold' : ''}`}>
      <td className="py-2 pr-3 text-ink-900">{s.lokasyon}</td>
      <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(s.iade)}</td>
      <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(s.netCiro)}</td>
      <td className={`py-2 px-3 text-right tnum ${artti ? 'text-neg-700' : 'text-ink-700'}`}>
        {s.oran === null ? '—' : oranMetni(s.oran, 3)}
      </td>
      <td className="py-2 pl-3 text-right tnum text-ink-500">{s.oncekiOran === null ? '—' : oranMetni(s.oncekiOran, 3)}</td>
    </tr>
  );
}

/** M5b — iade orani (spec §7.5). 990055 anahtari UyarilarBlok'unkiyle ayni (onceki pencere dahil) -> 0 ek istek. */
export function IadeDetay({ kaynaklar, donem, oncekiDonem, sube, kanal }: DetayGovdeProps) {
  const q = useSaSorgu<IadeOraniSatiri>(
    kaynaklar.iadeOrani, { donem, sube, kanal, onceki: oncekiDonem }, true, { onbellekten: true },
  );
  const veri = q.data?.data ?? [];

  let icerik;
  if (!kaynaklar.iadeOrani)
    icerik = <DetayMesaj testid="iade-detay-kaynak-yok">İade oranı raporu bu firmada tanımlı değil.</DetayMesaj>;
  else if (q.isLoading) icerik = <DetayYukleniyor testid="iade-detay-yukleniyor" />;
  else if (q.isError) icerik = <DetayMesaj testid="iade-detay-hata">İade oranları yüklenemedi.</DetayMesaj>;
  else if (veri.length === 0) icerik = <DetayMesaj testid="iade-detay-bos">Dönemde satış yok.</DetayMesaj>;
  else {
    const t = iadeTablosu(veri);
    icerik = (
      <div className="overflow-x-auto">
        <table data-testid="iade-detay-tablo" className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="text-[12px] text-ink-500 border-b border-border">
              <th className="text-left font-medium py-2 pr-3">Şube</th>
              <th className="text-right font-medium py-2 px-3">İade</th>
              <th className="text-right font-medium py-2 px-3">Net ciro</th>
              <th className="text-right font-medium py-2 px-3">Oran</th>
              <th className="text-right font-medium py-2 pl-3">Önceki oran</th>
            </tr>
          </thead>
          <tbody>
            {t.satirlar.map((s) => (
              <Satir key={s.lokasyon} s={s} testid={`iade-detay-satir-${s.lokasyon}`} />
            ))}
          </tbody>
          <tfoot>
            <Satir s={t.toplam} testid="iade-detay-toplam" kalin />
          </tfoot>
        </table>
      </div>
    );
  }

  return <div data-testid="iade-detay">{icerik}</div>;
}
